/**
 * Incremental sentence segmentation over streaming assistant tokens.
 *
 * feed(delta) appends text and returns newly-completed sentences; a sentence
 * ends at . ! ? , Arabic ؟ ، ۔ , or a newline. Fragments shorter than minChars
 * are held and coalesced with the next, so we don't fire a /speak/ call per
 * tiny fragment. flush() returns the trailing remainder once the stream ends.
 *
 * NOTE: default minChars is 1, not 12 as the original spec prose suggested —
 * 12 is mathematically incompatible with the spec's own test suite (e.g. a
 * 5-char "Done." or an 8-char "Line one" must emit immediately under the
 * default). Coalescing short fragments is opt-in via an explicit minChars
 * (see the `{ minChars: 12 }` test).
 */

const TERMINATORS = /[.!?؟،۔\n]/;

export interface SentenceChunker {
  feed(delta: string): string[];
  flush(): string | null;
}

export function createSentenceChunker(opts: { minChars?: number } = {}): SentenceChunker {
  const minChars = opts.minChars ?? 1;
  let buffer = "";
  let held = "";

  const emit = (sentence: string, out: string[]): void => {
    const candidate = (held + sentence).trim();
    if (!candidate) return;
    if (candidate.length < minChars) {
      held = held + sentence; // keep accumulating until it's worth speaking
      return;
    }
    held = "";
    out.push(candidate);
  };

  return {
    feed(delta: string): string[] {
      buffer += delta;
      const out: string[] = [];
      let match: RegExpExecArray | null;
      // Repeatedly peel the leading "…<terminator>" off the buffer.
      while ((match = TERMINATORS.exec(buffer)) !== null) {
        const end = match.index + 1;
        const piece = buffer.slice(0, end);
        buffer = buffer.slice(end);
        // Newline terminators shouldn't keep a literal \n in the spoken text.
        emit(piece.replace(/\n/g, " "), out);
      }
      return out;
    },
    flush(): string | null {
      const remainder = (held + buffer).trim();
      held = "";
      buffer = "";
      return remainder || null;
    },
  };
}
